import { verifyQrToken } from "@sunfish/api/check-in/qr-token";
import { useToast } from "@sunfish/ui";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { getDeviceMeta, getDeviceToken } from "~/lib/device-session";
import type { CachedAttendee, LocalCheckIn } from "~/lib/offline/db";
import {
	enqueueCheckIn,
	getAttendee,
	getAttendeeByCode,
	getCheckInsFor,
	getMeta,
	searchRoster,
	setMeta,
	statusFor,
} from "~/lib/offline/store";
import { useSync } from "~/providers/sync-provider";
import { useTRPC } from "~utils/trpc/query-client";
import { AttendeeCard } from "../components/attendee-card";
import { SyncIndicator } from "../components/sync-indicator";
import { useQrScanner } from "../components/use-qr-scanner";
import KioskScreen from "./kiosk";

type CheckInSettings = {
	mode: "scan" | "search" | "both";
	checkoutEnabled: boolean;
	visibleFields: { email: boolean };
};

const SETTINGS_META_KEY = "settings";

type Found = {
	attendeeId: string;
	firstName: string | null;
	lastName: string | null;
	email: string | null;
	status: "in" | "out" | "none";
	lastEventAt: string | null;
	lastClientId: string | null;
	claimedName?: string | null;
	claimedEmail?: string | null;
};

type RosterResult = {
	attendeeId: string;
	firstName: string | null;
	lastName: string | null;
	email: string | null;
	status: "in" | "out" | "none";
};

async function toFound(eventId: string, attendee: CachedAttendee): Promise<Found> {
	const events = await getCheckInsFor(eventId, attendee.attendeeId);
	const derived = statusFor(attendee.attendeeId, events);
	return {
		attendeeId: attendee.attendeeId,
		firstName: attendee.firstName,
		lastName: attendee.lastName,
		email: attendee.email,
		status: derived.status,
		lastEventAt: derived.lastEvent?.occurredAt ?? null,
		lastClientId: derived.lastEvent?.clientId ?? null,
	};
}

async function refreshTokenFound(eventId: string, prev: Found): Promise<Found> {
	const events = await getCheckInsFor(eventId, prev.attendeeId);
	const derived = statusFor(prev.attendeeId, events);
	return {
		...prev,
		status: derived.status,
		lastEventAt: derived.lastEvent?.occurredAt ?? null,
		lastClientId: derived.lastEvent?.clientId ?? null,
	};
}

export default function Operate() {
	const navigate = useNavigate();
	const meta = getDeviceMeta();
	const eventId = meta?.eventId;

	useEffect(() => {
		if (!getDeviceToken() || !eventId) {
			navigate("/provision", { replace: true });
		}
	}, [navigate, eventId]);

	if (!eventId) return null;

	if (meta?.mode === "kiosk") return <KioskScreen eventId={eventId} />;

	return <OperateScreen eventId={eventId} qrPublicKey={meta?.qrPublicKey ?? null} />;
}

function OperateScreen({ eventId, qrPublicKey }: { eventId: string; qrPublicKey: string | null }) {
	const trpc = useTRPC();
	const { toast } = useToast();
	const { syncNow } = useSync();

	const { data: serverSettings } = trpc.checkIn.getSettings.useQuery(
		{ eventId },
		{ retry: false },
	);
	const [settings, setSettings] = useState<CheckInSettings | null>(null);

	useEffect(() => {
		if (serverSettings) {
			setSettings(serverSettings);
			void setMeta(eventId, SETTINGS_META_KEY, JSON.stringify(serverSettings));
		}
	}, [serverSettings, eventId]);

	useEffect(() => {
		if (serverSettings) return;
		void (async () => {
			const cached = await getMeta(eventId, SETTINGS_META_KEY);
			if (cached) setSettings(JSON.parse(cached) as CheckInSettings);
		})();
	}, [serverSettings, eventId]);

	const [current, setCurrent] = useState<Found | null>(null);
	const [currentSource, setCurrentSource] = useState<"scan" | "search">("scan");
	const [search, setSearch] = useState("");
	const [searchResults, setSearchResults] = useState<RosterResult[]>([]);

	useEffect(() => {
		if (search.length <= 1) {
			setSearchResults([]);
			return;
		}
		let cancelled = false;
		void (async () => {
			const rows = await searchRoster(eventId, search);
			const withStatus = await Promise.all(
				rows.map(async (r) => {
					const events = await getCheckInsFor(eventId, r.attendeeId);
					return {
						attendeeId: r.attendeeId,
						firstName: r.firstName,
						lastName: r.lastName,
						email: r.email,
						status: statusFor(r.attendeeId, events).status,
					};
				}),
			);
			if (!cancelled) setSearchResults(withStatus);
		})();
		return () => {
			cancelled = true;
		};
	}, [search, eventId]);

	const resolveCode = useCallback(
		async (code: string) => {
			if (qrPublicKey) {
				const { valid, payload } = await verifyQrToken(code, qrPublicKey);
				if (valid && payload && payload.eid === eventId) {
					const cached = await getAttendee(eventId, payload.aid);
					if (cached) {
						setCurrent(await toFound(eventId, cached));
					} else {
						const events = await getCheckInsFor(eventId, payload.aid);
						const derived = statusFor(payload.aid, events);
						const [firstName, ...rest] = payload.n.split(" ");
						setCurrent({
							attendeeId: payload.aid,
							firstName: firstName || null,
							lastName: rest.length ? rest.join(" ") : null,
							email: payload.e,
							status: derived.status,
							lastEventAt: derived.lastEvent?.occurredAt ?? null,
							lastClientId: derived.lastEvent?.clientId ?? null,
							claimedName: payload.n || null,
							claimedEmail: payload.e,
						});
					}
					setCurrentSource("scan");
					return;
				}
			}

			const attendee = await getAttendeeByCode(eventId, code);
			if (!attendee) {
				toast({
					title: "Not found (offline)",
					description: "No cached attendee matches that code.",
					variant: "destructive",
				});
				return;
			}
			setCurrent(await toFound(eventId, attendee));
			setCurrentSource("scan");
		},
		[eventId, qrPublicKey, toast],
	);

	const videoRef = useQrScanner(resolveCode, settings?.mode !== "search");

	const enqueue = useCallback(
		async (row: Omit<LocalCheckIn, "createdAt" | "synced">) => {
			const now = new Date().toISOString();
			await enqueueCheckIn(eventId, { ...row, createdAt: now, synced: 0 });
		},
		[eventId],
	);

	const doRecord = async (direction: "in" | "out", source: "scan" | "search") => {
		if (!current) return;
		await enqueue({
			clientId: crypto.randomUUID(),
			attendeeId: current.attendeeId,
			direction,
			voidsClientId: null,
			occurredAt: new Date().toISOString(),
			source,
			claimedName: current.claimedName ?? null,
			claimedEmail: current.claimedEmail ?? null,
		});
		const attendee = await getAttendee(eventId, current.attendeeId);
		if (attendee) setCurrent(await toFound(eventId, attendee));
		else setCurrent(await refreshTokenFound(eventId, current));
		toast({ title: direction === "in" ? "Checked in" : "Checked out" });
		void syncNow();
	};

	const doUndo = async () => {
		if (!current?.lastClientId) return;
		await enqueue({
			clientId: crypto.randomUUID(),
			attendeeId: current.attendeeId,
			direction: "out",
			voidsClientId: current.lastClientId,
			occurredAt: new Date().toISOString(),
			source: "manual",
		});
		const attendee = await getAttendee(eventId, current.attendeeId);
		if (attendee) setCurrent(await toFound(eventId, attendee));
		else setCurrent(await refreshTokenFound(eventId, current));
		toast({ title: "Check-in undone" });
		void syncNow();
	};

	const showScan = settings?.mode !== "search";
	const showSearch = settings?.mode !== "scan";

	return (
		<div className="min-h-dvh bg-slate-950 text-slate-100 p-4 flex flex-col md:flex-row gap-4">
			<div className="absolute top-4 right-4 z-10">
				<SyncIndicator />
			</div>
			{showScan && (
				<div className="md:w-1/2 flex items-center justify-center">
					<video ref={videoRef} className="w-full max-w-md rounded-xl bg-black aspect-square object-cover" />
				</div>
			)}
			<div className="md:flex-1 space-y-4">
				{showSearch && (
					<>
						<input
							className="w-full rounded bg-slate-800 px-3 py-2"
							placeholder="Search name or email…"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
						<div className="space-y-1">
							{searchResults.slice(0, 8).map((r) => (
								<button
									key={r.attendeeId}
									type="button"
									className="w-full text-left rounded bg-slate-800 px-3 py-2 flex justify-between"
									onClick={async () => {
										const attendee = await getAttendee(eventId, r.attendeeId);
										if (attendee) {
											setCurrent(await toFound(eventId, attendee));
											setCurrentSource("search");
										}
									}}
								>
									<span>{`${r.firstName ?? ""} ${r.lastName ?? ""}`.trim() || "Unknown"}</span>
									<span>{r.status === "in" ? "🟢" : "⚪"}</span>
								</button>
							))}
						</div>
					</>
				)}
				{current && (
					<AttendeeCard
						attendee={current}
						showEmail={settings?.visibleFields.email ?? true}
						checkoutEnabled={settings?.checkoutEnabled ?? false}
						onCheckIn={() => doRecord("in", currentSource)}
						onCheckOut={() => doRecord("out", currentSource)}
						onUndo={doUndo}
					/>
				)}
			</div>
		</div>
	);
}
